import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCcw, Plus, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { useStaffAuth } from '../../context/StaffAuthContext';
import { apiErrorMessage } from '../../lib/apiError';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';

const ROLES = [
  { key: 'rm', label: 'Relationship Manager' },
  { key: 'manager', label: 'Sales Manager' },
  { key: 'admin', label: 'Admin' },
];

export default function CrmStaff() {
  const { staff, authHeader, API } = useStaffAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/staff`, { headers: authHeader() });
      setTeam(res.data.staff || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load staff'));
    } finally {
      setLoading(false);
    }
  }, [API, authHeader]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/crm/staff/${id}`, patch, { headers: authHeader() });
      setTeam((prev) => prev.map((s) => (s.id === id ? res.data.staff : s)));
      toast.success('Team member updated');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Update failed'));
    }
  };

  if (staff.role !== 'admin') {
    return (
      <div className="p-10 max-w-[900px] mx-auto">
        <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 p-10 text-center">
          <UserCog className="w-8 h-8 mx-auto text-[#0B3B2E]/40" />
          <h1 className="mt-4 font-serif text-[24px] text-[#0B3B2E]">Admin only</h1>
          <p className="mt-2 text-[13px] text-[#0E1F1A]/60">Only admins can manage team members.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-[1200px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">CRM</p>
          <h1 className="mt-2 font-serif text-[32px] lg:text-[40px] leading-tight text-[#0B3B2E]">Team</h1>
          <p className="mt-1 text-[13px] text-[#0E1F1A]/60">Add and manage internal staff, roles and access.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline"
            className="h-10 rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E] hover:bg-[#0B3B2E]/5">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={() => setOpen(true)}
            className="h-10 rounded-full bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F]">
            <Plus className="w-4 h-4 mr-1.5" /> Add member
          </Button>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-[#0B3B2E]/10 overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-[#0B3B2E]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#0B3B2E]/5 text-[#0B3B2E]">
              <tr>
                <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Status</Th><Th>Created</Th>
              </tr>
            </thead>
            <tbody>
              {team.map((s) => (
                <tr key={s.id} className="border-t border-[#0B3B2E]/8 hover:bg-[#F5F1E8]/40">
                  <Td className="font-medium text-[#0B3B2E]">{s.name}</Td>
                  <Td>{s.email}</Td>
                  <Td>
                    <select value={s.role} onChange={(e) => update(s.id, { role: e.target.value })}
                      disabled={s.id === staff.id}
                      className="h-8 rounded-md border border-[#0B3B2E]/15 bg-white px-2 text-[12px]">
                      {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </Td>
                  <Td>
                    <button
                      onClick={() => update(s.id, { active: !s.active })}
                      disabled={s.id === staff.id}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${
                        s.active ? 'bg-emerald-100 text-emerald-800' : 'bg-neutral-200 text-neutral-700'
                      } ${s.id === staff.id ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                      {s.active ? 'Active' : 'Deactivated'}
                    </button>
                  </Td>
                  <Td className="text-[#0E1F1A]/55">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString('en-GB') : '—'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewStaffModal open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">{children}</th>; }
function Td({ children, className = '' }) { return <td className={`px-4 py-3 text-[13px] text-[#0E1F1A]/85 ${className}`}>{children}</td>; }

function NewStaffModal({ open, onOpenChange, onCreated }) {
  const { authHeader, API } = useStaffAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'rm' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error('All fields are required');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    setBusy(true);
    try {
      await axios.post(`${API}/crm/staff`, form, { headers: authHeader() });
      toast.success('Team member added');
      setForm({ name: '', email: '', password: '', role: 'rm' });
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Create failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] bg-[#F5F1E8]">
        <DialogTitle asChild>
          <h2 className="font-serif text-[24px] text-[#0B3B2E]">Add team member</h2>
        </DialogTitle>
        <DialogDescription className="text-[13px] text-[#0E1F1A]/60">
          They will be able to sign in immediately with the credentials you set below.
        </DialogDescription>
        <form onSubmit={submit} className="space-y-3">
          <Row label="Name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></Row>
          <Row label="Email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Row>
          <Row label="Temporary password"><Input type="text" value={form.password} onChange={(e) => set('password', e.target.value)} placeholder="Minimum 6 characters" /></Row>
          <Row label="Role">
            <select value={form.role} onChange={(e) => set('role', e.target.value)}
              className="h-10 w-full rounded-md border border-[#0B3B2E]/20 bg-white px-3 text-[14px]">
              {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </Row>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E]">Cancel</Button>
            <Button type="submit" disabled={busy}
              className="rounded-full bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add member'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[12px] text-[#0E1F1A]/65">{label}</Label>
      {children}
    </div>
  );
}
